import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import JsPDF from 'jspdf';
import { toast } from 'react-toastify';
import { getAllBeneficiaries, getList } from '../../API/apiService';
import DateRangeFilter from '../../components/dateRangeFilter';
import {
  matchesBeneficiaryByAdminScope,
  normalizeAdminScope
} from '../../utils/adminScope';
import {
  clearDateFiltersFromSearchParams,
  filterRecordsByDateRange,
  formatDateRangeLabel,
  getDateFiltersFromSearchParams,
  getRecordCreatedDate,
  setDateFilterInSearchParams
} from '../../utils/dateFilters';
import {
  drawPdfCard,
  drawPdfDistributionCard,
  formatPdfFileDate,
  formatPdfReportDate
} from '../../utils/pdfReport';
import './style.scss';

const PROCESS_COPY = {
  general: {
    eyebrow: 'Analisis general de Caracterizados',
    title: 'Escuelas de Participacion + Nada de Nervios',
    description:
      'Lectura consolidada de la poblacion caracterizada en ambos procesos y sus principales condiciones registradas.'
  },
  pf: {
    eyebrow: 'Analisis de Caracterizados',
    title: 'Escuelas de Participacion',
    description:
      'Lectura general de la poblacion caracterizada, su composicion y principales condiciones registradas, tomando como referencia la comuna o corregimiento donde vive cada persona.'
  },
  ndn: {
    eyebrow: 'Analisis de Caracterizados',
    title: 'Nada de Nervios',
    description:
      'Lectura general de la poblacion caracterizada, su composicion y principales condiciones registradas, tomando como referencia la comuna o corregimiento donde vive cada persona.'
  },
  cs: {
    eyebrow: 'Analisis de Caracterizados',
    title: 'Control Social',
    description:
      'Lectura general de la poblacion caracterizada en los procesos formativos de Control Social.'
  },
  cop: {
    eyebrow: 'Analisis de Caracterizados',
    title: 'Centro de Orientacion Pedagogica',
    description:
      'Lectura general de la poblacion caracterizada en las asesorias COP, su composicion y principales condiciones registradas.'
  },
  default: {
    eyebrow: 'Analisis de Caracterizados',
    title: 'Proceso formativo',
    description:
      'Lectura general de la poblacion caracterizada y sus principales rasgos, tomando como referencia el territorio donde vive la poblacion registrada.'
  }
};

const PROCESS_REPORT_LABELS = {
  general: 'Escuelas de Participacion + Nada de Nervios',
  pf: 'Escuelas de Participacion',
  ndn: 'Nada de Nervios',
  cs: 'Control Social',
  cop: 'Centro de Orientacion Pedagogica'
};

const MIN_VALID_ANALYSIS_AGE = 6;
const TEACHER_GROUP_CODE_PATTERN = /GD\d+$/;

const AGE_RANGES = [
  { label: '6 a 11 anos', min: 6, max: 11 },
  { label: '12 a 17 anos', min: 12, max: 17 },
  { label: '18 a 28 anos', min: 18, max: 28 },
  { label: '29 a 59 anos', min: 29, max: 59 },
  { label: '60+ anos', min: 60, max: Number.POSITIVE_INFINITY }
];

function normalizeText(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function getGroupTypeText(group) {
  const ageRange = String(group?.age_range ?? group?.ageRange ?? '').trim();
  if (ageRange) return ageRange;

  const legacyDescription = String(group?.description ?? '').trim();
  const normalizedLegacyDescription = normalizeText(legacyDescription);
  return ['docente', 'comunidad', 'estudiante'].some(type =>
    normalizedLegacyDescription.includes(type)
  )
    ? legacyDescription
    : '';
}

function isTeacherGroup(group) {
  const groupType = normalizeText(getGroupTypeText(group));
  if (groupType.includes('docente')) return true;

  const code = String(group?.code || '').trim().toUpperCase();
  return TEACHER_GROUP_CODE_PATTERN.test(code);
}

function shouldIncludeBeneficiaryInCharacterizedAnalysis(beneficiary, scope) {
  if (scope !== 'pf') return true;
  return !isTeacherGroup(beneficiary?.group);
}

function formatNumber(value) {
  return new Intl.NumberFormat('es-CO').format(Number(value ?? 0));
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return '0%';
  return `${Math.round(value)}%`;
}

function resolveYesNo(value) {
  if (
    value === true ||
    value === 1 ||
    ['si', 'yes', 'true'].includes(normalizeText(value))
  ) {
    return true;
  }

  if (
    value === false ||
    value === 0 ||
    ['no', 'false'].includes(normalizeText(value))
  ) {
    return false;
  }

  return null;
}

function resolveGenderValue(beneficiary, genders) {
  const rawValue = beneficiary?.gender_value ?? beneficiary?.gender ?? '';

  if (!rawValue) {
    return '';
  }

  const found = Array.isArray(genders)
    ? genders.find(item =>
        item?.id?.toString() === rawValue?.toString() ||
        item?.code?.toString() === rawValue?.toString() ||
        item?.value?.toString() === rawValue?.toString()
      )
    : null;

  return String(found?.value ?? rawValue).trim();
}

function getBeneficiaryAge(beneficiary) {
  const rawAge = Number(beneficiary?.age);
  if (Number.isFinite(rawAge) && rawAge >= 0) {
    return rawAge;
  }

  const birthDate = beneficiary?.birth_date;
  if (!birthDate) {
    return null;
  }

  const birthday = new Date(birthDate);
  if (Number.isNaN(birthday.getTime())) {
    return null;
  }

  const now = new Date();
  let age = now.getFullYear() - birthday.getFullYear();
  const month = now.getMonth() - birthday.getMonth();

  if (month < 0 || (month === 0 && now.getDate() < birthday.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

function isValidAnalysisAge(age) {
  return Number.isFinite(age) && age >= MIN_VALID_ANALYSIS_AGE;
}

function buildDistribution(values, { limit = 5, emptyLabel = 'Sin dato' } = {}) {
  const countMap = values.reduce((accumulator, value) => {
    const label = String(value ?? '').trim() || emptyLabel;
    accumulator[label] = (accumulator[label] || 0) + 1;
    return accumulator;
  }, {});

  const items = Object.entries(countMap)
    .map(([label, count]) => ({ label, count }))
    .sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }

      return left.label.localeCompare(right.label, 'es', {
        sensitivity: 'base'
      });
    });

  if (items.length <= limit) {
    const total = items.reduce((sum, item) => sum + item.count, 0);
    return items.map(item => ({
      ...item,
      percent: total > 0 ? (item.count / total) * 100 : 0
    }));
  }

  const visibleItems = items.slice(0, limit - 1);
  const remainingItems = items.slice(limit - 1);
  const remainingCount = remainingItems.reduce((sum, item) => sum + item.count, 0);
  const total = items.reduce((sum, item) => sum + item.count, 0);

  return [...visibleItems, { label: 'Otros', count: remainingCount }]
    .map(item => ({
      ...item,
      percent: total > 0 ? (item.count / total) * 100 : 0
    }));
}

function splitMultipleSelections(values) {
  return values.flatMap(value => {
    const selections = String(value ?? '')
      .split('|')
      .map(selection => selection.trim())
      .filter(Boolean);

    return selections.length > 0 ? selections : [''];
  });
}

function buildAgeDistribution(ages) {
  const validAges = ages.filter(isValidAnalysisAge);
  const total = validAges.length;

  return AGE_RANGES.map(range => {
    const count = validAges.filter(
      age => age >= range.min && age <= range.max
    ).length;

    return {
      label: range.label,
      count,
      percent: total > 0 ? (count / total) * 100 : 0
    };
  });
}

function createEmptySummary() {
  return {
    totalCharacterized: 0,
    averageAge: null,
    women: 0,
    men: 0,
    otherGenders: 0,
    studiesCurrently: 0,
    lgbtiqPopulation: 0,
    victimRegistry: 0,
    farmerPopulation: 0,
    nnaRightsProcess: 0,
    uniqueCommunes: 0,
    sexDistribution: [],
    ageDistribution: [],
    scholarshipDistribution: [],
    ethnicDistribution: [],
    communeDistribution: [],
    occupationDistribution: [],
    victimPopulationDistribution: [],
    disabilityDistribution: [],
    riskDistribution: []
  };
}

function buildCharacterizedSummary(beneficiaries, genders) {
  const normalizedBeneficiaries = Array.isArray(beneficiaries) ? beneficiaries : [];
  const ages = normalizedBeneficiaries
    .map(getBeneficiaryAge)
    .filter(isValidAnalysisAge);
  const averageAge =
    ages.length > 0
      ? ages.reduce((sum, age) => sum + age, 0) / ages.length
      : null;

  const genderLabels = normalizedBeneficiaries.map(beneficiary =>
    resolveGenderValue(beneficiary, genders)
  );

  const women = genderLabels.filter(label =>
    normalizeText(label).includes('mujer') ||
    normalizeText(label).includes('femen')
  ).length;
  const men = genderLabels.filter(label =>
    normalizeText(label).includes('hombre') ||
    normalizeText(label).includes('mascul')
  ).length;
  const otherGenders = Math.max(normalizedBeneficiaries.length - women - men, 0);

  const studiesCurrently = normalizedBeneficiaries.filter(beneficiary =>
    resolveYesNo(beneficiary?.study_currently) === true
  ).length;

  const lgbtiqPopulation = normalizedBeneficiaries.filter(beneficiary =>
    resolveYesNo(beneficiary?.lgbtiq) === true
  ).length;

  const victimRegistry = normalizedBeneficiaries.filter(beneficiary =>
    resolveYesNo(beneficiary?.victim_registry) === true
  ).length;
  const farmerPopulation = normalizedBeneficiaries.filter(beneficiary =>
    resolveYesNo(beneficiary?.farmer) === true
  ).length;
  const nnaRightsProcess = normalizedBeneficiaries.filter(beneficiary =>
    resolveYesNo(beneficiary?.nna_rights_process) === true
  ).length;

  const communeLabels = normalizedBeneficiaries
    .map(beneficiary => String(beneficiary?.municipality?.name ?? '').trim())
    .filter(Boolean);

  return {
    totalCharacterized: normalizedBeneficiaries.length,
    averageAge,
    women,
    men,
    otherGenders,
    studiesCurrently,
    lgbtiqPopulation,
    victimRegistry,
    farmerPopulation,
    nnaRightsProcess,
    uniqueCommunes: new Set(communeLabels).size,
    sexDistribution: buildDistribution(genderLabels, { limit: 4 }),
    ageDistribution: buildAgeDistribution(ages),
    scholarshipDistribution: buildDistribution(
      normalizedBeneficiaries.map(beneficiary => beneficiary?.scholarship_value),
      { limit: 5 }
    ),
    ethnicDistribution: buildDistribution(
      normalizedBeneficiaries.map(beneficiary => beneficiary?.ethnic_group_value),
      { limit: 5 }
    ),
    communeDistribution: buildDistribution(communeLabels, { limit: 6 }),
    occupationDistribution: buildDistribution(
      normalizedBeneficiaries.map(beneficiary => beneficiary?.occupation_value),
      { limit: 5 }
    ),
    victimPopulationDistribution: buildDistribution(
      normalizedBeneficiaries.map(
        beneficiary => beneficiary?.victim_population_value
      ),
      { limit: 5 }
    ),
    disabilityDistribution: buildDistribution(
      splitMultipleSelections(
        normalizedBeneficiaries.map(
          beneficiary => beneficiary?.disability_value || beneficiary?.disability
        )
      ),
      { limit: 5 }
    ),
    riskDistribution: buildDistribution(
      splitMultipleSelections(
        normalizedBeneficiaries.map(
          beneficiary => beneficiary?.risk_factor_value || beneficiary?.risk_factor
        )
      ),
      { limit: 5 }
    )
  };
}

function SummaryCard({ label, value, helper }) {
  return (
    <article className='admin-characterized-analysis__summary-card'>
      <span className='admin-characterized-analysis__summary-label'>{label}</span>
      <strong className='admin-characterized-analysis__summary-value'>
        {value}
      </strong>
      <span className='admin-characterized-analysis__summary-helper'>
        {helper}
      </span>
    </article>
  );
}

function DistributionCard({
  title,
  items,
  loading,
  emptyMessage = 'Sin datos disponibles.'
}) {
  return (
    <article className='admin-characterized-analysis__distribution-card'>
      <h2 className='admin-characterized-analysis__section-title'>{title}</h2>

      {loading ? (
        <p className='admin-characterized-analysis__empty'>Cargando datos...</p>
      ) : items.length > 0 ? (
        <div className='admin-characterized-analysis__distribution-list'>
          {items.map(item => (
            <div
              key={`${title}-${item.label}`}
              className='admin-characterized-analysis__distribution-item'>
              <div className='admin-characterized-analysis__distribution-head'>
                <span>{item.label}</span>
                <strong>{formatNumber(item.count)}</strong>
              </div>
              <div className='admin-characterized-analysis__distribution-bar'>
                <span
                  style={{
                    width: `${Math.max(item.percent, item.count > 0 ? 8 : 0)}%`
                  }}></span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className='admin-characterized-analysis__empty'>{emptyMessage}</p>
      )}
    </article>
  );
}

function AdminCharacterizedAnalysis() {
  const [searchParams, setSearchParams] = useSearchParams();
  const adminScope = normalizeAdminScope(searchParams.get('scope'));
  const dateFilters = getDateFiltersFromSearchParams(searchParams);
  const dateFrom = dateFilters.dateFrom;
  const dateTo = dateFilters.dateTo;
  const copy = PROCESS_COPY[adminScope] || PROCESS_COPY.default;
  const [summary, setSummary] = useState(createEmptySummary);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadAnalysis() {
      setLoading(true);

      const [beneficiariesResponse, gendersResponse] = await Promise.all([
        getAllBeneficiaries(),
        getList('gender')
      ]);

      if (!isMounted) {
        return;
      }

      const scopedBeneficiaries = Array.isArray(beneficiariesResponse)
        ? beneficiariesResponse.filter(beneficiary =>
            matchesBeneficiaryByAdminScope(beneficiary, adminScope) &&
            shouldIncludeBeneficiaryInCharacterizedAnalysis(
              beneficiary,
              adminScope
            )
          )
        : [];
      const activeDateFilters = { dateFrom, dateTo };
      const dateFilteredBeneficiaries = filterRecordsByDateRange(
        scopedBeneficiaries,
        activeDateFilters,
        getRecordCreatedDate
      );

      setSummary(
        buildCharacterizedSummary(dateFilteredBeneficiaries, gendersResponse)
      );
      setLoading(false);
    }

    loadAnalysis();

    return () => {
      isMounted = false;
    };
  }, [adminScope, dateFrom, dateTo]);

  function handleDateFilterChange(key, value) {
    setSearchParams(
      setDateFilterInSearchParams(searchParams, key, value),
      { replace: true }
    );
  }

  function handleClearDateFilters() {
    setSearchParams(clearDateFiltersFromSearchParams(searchParams), {
      replace: true
    });
  }

  const totalCharacterized = summary.totalCharacterized || 0;
  const womenPercent = totalCharacterized
    ? (summary.women / totalCharacterized) * 100
    : 0;
  const menPercent = totalCharacterized
    ? (summary.men / totalCharacterized) * 100
    : 0;

  function handleDownloadAnalysis() {
    if (loading) {
      return;
    }

    setIsDownloading(true);

    try {
      const generatedAt = new Date();
      const processLabel =
        PROCESS_REPORT_LABELS[adminScope] || copy.title || 'Proceso formativo';
      const fileName = `analisis-caracterizacion-${adminScope || 'general'}-${formatPdfFileDate(
        generatedAt
      )}.pdf`;
      const doc = new JsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4'
      });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 30;
      const gutter = 12;
      const contentWidth = pageWidth - margin * 2;
      const summaryCardWidth = (contentWidth - gutter * 3) / 4;
      const summaryCardHeight = 86;
      const distributionCardWidth = (contentWidth - gutter * 2) / 3;
      const distributionCardHeight = 170;

      const summaryCards = [
        {
          title: 'Total caracterizados',
          value: formatNumber(summary.totalCharacterized),
          helper: 'Personas registradas en caracterizacion'
        },
        {
          title: 'Promedio de edad',
          value:
            summary.averageAge === null ? '--' : `${summary.averageAge.toFixed(1)}`,
          helper: 'Promedio calculado con edades validas desde 6 anos'
        },
        {
          title: 'Mujeres',
          value: formatNumber(summary.women),
          helper: formatPercent(womenPercent)
        },
        {
          title: 'Hombres',
          value: formatNumber(summary.men),
          helper: formatPercent(menPercent)
        },
        {
          title: 'Otros / sin dato',
          value: formatNumber(summary.otherGenders),
          helper: 'Registros no clasificados como mujer u hombre'
        },
        {
          title: 'Estudian actualmente',
          value: formatNumber(summary.studiesCurrently),
          helper: 'Respuesta afirmativa en estudio actual'
        },
        {
          title: 'Poblacion LGBTIQ+',
          value: formatNumber(summary.lgbtiqPopulation),
          helper: 'Respuesta afirmativa en caracterizacion'
        },
        {
          title: 'Comunas de residencia',
          value: formatNumber(summary.uniqueCommunes),
          helper: 'Territorios donde vive la poblacion caracterizada'
        }
      ];

      doc.setFillColor(237, 248, 251);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');

      doc.setFillColor(24, 57, 85);
      doc.roundedRect(margin, margin, contentWidth, 82, 24, 24, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      doc.setTextColor(255, 255, 255);
      doc.text(`Analisis de caracterizados - ${processLabel}`, margin + 24, margin + 34);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(231, 244, 250);
      doc.text(
        `Reporte generado el ${formatPdfReportDate(generatedAt)}`,
        margin + 24,
        margin + 56
      );

      doc.setFontSize(9);
      doc.text(formatDateRangeLabel(dateFilters), margin + 24, margin + 72);

      summaryCards.forEach((card, index) => {
        const column = index % 4;
        const row = Math.floor(index / 4);
        const x = margin + column * (summaryCardWidth + gutter);
        const y = margin + 98 + row * (summaryCardHeight + gutter);

        drawPdfCard(doc, x, y, summaryCardWidth, summaryCardHeight, card);
      });

      const flagCards = [
        {
          title: 'Cuenta con RUV',
          value: formatNumber(summary.victimRegistry),
          helper: 'Respuesta afirmativa en registro'
        },
        {
          title: 'Se identifica como campesino',
          value: formatNumber(summary.farmerPopulation),
          helper: 'Respuesta afirmativa en caracterizacion'
        },
        {
          title: 'NNA en restablecimiento de derechos',
          value: formatNumber(summary.nnaRightsProcess),
          helper: 'Respuesta afirmativa en caracterizacion'
        }
      ];

      flagCards.forEach((card, index) => {
        const x = margin + index * (distributionCardWidth + gutter);
        drawPdfCard(doc, x, margin + 288, distributionCardWidth, 82, card);
      });

      doc.addPage('a4', 'landscape');
      doc.setFillColor(237, 248, 251);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');

      doc.setFillColor(24, 57, 85);
      doc.roundedRect(margin, margin, contentWidth, 62, 20, 20, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text('Distribuciones principales de caracterizacion', margin + 20, margin + 38);

      const distributionCards = [
        { title: 'Sexo al nacer', items: summary.sexDistribution },
        { title: 'Rangos de edad', items: summary.ageDistribution },
        {
          title: 'Comuna o corregimiento de residencia',
          items: summary.communeDistribution
        },
        { title: 'Nivel de escolaridad', items: summary.scholarshipDistribution },
        { title: 'Etnia', items: summary.ethnicDistribution },
        { title: 'Ocupacion actual', items: summary.occupationDistribution }
      ];

      distributionCards.forEach((card, index) => {
        const column = index % 3;
        const row = Math.floor(index / 3);
        const x = margin + column * (distributionCardWidth + gutter);
        const y = margin + 82 + row * (distributionCardHeight + gutter);

        drawPdfDistributionCard(
          doc,
          x,
          y,
          distributionCardWidth,
          distributionCardHeight,
          {
            title: card.title,
            items: card.items,
            accentColor: [24, 176, 229]
          }
        );
      });

      doc.save(fileName);
    } catch (error) {
      console.error(error);
      toast('No fue posible descargar el analisis de caracterizacion', {
        type: 'error'
      });
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <section className='admin-characterized-analysis'>
      <div className='admin-characterized-analysis__shell'>
        <div className='admin-characterized-analysis__toolbar'>
          <DateRangeFilter
            dateFrom={dateFilters.dateFrom}
            dateTo={dateFilters.dateTo}
            onChange={handleDateFilterChange}
            onClear={handleClearDateFilters}
            disabled={loading}
          />
          <button
            type='button'
            className='admin-characterized-analysis__download-button'
            onClick={handleDownloadAnalysis}
            disabled={loading || isDownloading}>
            <span className='material-symbols-rounded' aria-hidden='true'>
              download
            </span>
            {isDownloading ? 'Preparando archivo...' : 'Descargar analisis'}
          </button>
        </div>

        <section className='admin-characterized-analysis__hero'>
          <span className='admin-characterized-analysis__eyebrow'>
            {copy.eyebrow}
          </span>
          <h1 className='admin-characterized-analysis__title'>{copy.title}</h1>
          <p className='admin-characterized-analysis__description'>
            {copy.description}
          </p>
        </section>

        <section className='admin-characterized-analysis__summary-grid'>
          <SummaryCard
            label='Total caracterizados'
            value={loading ? '--' : formatNumber(summary.totalCharacterized)}
            helper='Personas registradas en caracterizacion'
          />
          <SummaryCard
            label='Promedio de edad'
            value={
              loading || summary.averageAge === null
                ? '--'
                : `${summary.averageAge.toFixed(1)}`
            }
            helper='Promedio calculado con edades validas desde 6 anos'
          />
          <SummaryCard
            label='Mujeres'
            value={loading ? '--' : formatNumber(summary.women)}
            helper={loading ? '---' : formatPercent(womenPercent)}
          />
          <SummaryCard
            label='Hombres'
            value={loading ? '--' : formatNumber(summary.men)}
            helper={loading ? '---' : formatPercent(menPercent)}
          />
          <SummaryCard
            label='Otros / sin dato'
            value={loading ? '--' : formatNumber(summary.otherGenders)}
            helper='Registros no clasificados como mujer u hombre'
          />
          <SummaryCard
            label='Estudian actualmente'
            value={loading ? '--' : formatNumber(summary.studiesCurrently)}
            helper='Respuesta afirmativa en estudio actual'
          />
          <SummaryCard
            label='Poblacion LGBTIQ+'
            value={loading ? '--' : formatNumber(summary.lgbtiqPopulation)}
            helper='Respuesta afirmativa en caracterizacion'
          />
          <SummaryCard
            label='Comunas de residencia'
            value={loading ? '--' : formatNumber(summary.uniqueCommunes)}
            helper='Lugar donde vive la poblacion caracterizada, aunque el grupo se haya creado en otra comuna'
          />
        </section>

        <section className='admin-characterized-analysis__sections'>
          <DistributionCard
            title='Sexo al nacer'
            items={summary.sexDistribution}
            loading={loading}
            emptyMessage='No hay datos de sexo al nacer disponibles.'
          />
          <DistributionCard
            title='Rangos de edad'
            items={summary.ageDistribution}
            loading={loading}
            emptyMessage='No hay edades validas desde 6 anos para calcular rangos.'
          />
          <DistributionCard
            title='Nivel de escolaridad'
            items={summary.scholarshipDistribution}
            loading={loading}
            emptyMessage='No hay datos de escolaridad disponibles.'
          />
          <DistributionCard
            title='Etnia'
            items={summary.ethnicDistribution}
            loading={loading}
            emptyMessage='No hay datos de etnia disponibles.'
          />
          <DistributionCard
            title='Comuna o corregimiento de residencia'
            items={summary.communeDistribution}
            loading={loading}
            emptyMessage='No hay datos territoriales disponibles.'
          />
          <DistributionCard
            title='Ocupacion actual'
            items={summary.occupationDistribution}
            loading={loading}
            emptyMessage='No hay datos de ocupacion disponibles.'
          />
          <DistributionCard
            title='Poblacion victima'
            items={summary.victimPopulationDistribution}
            loading={loading}
            emptyMessage='No hay datos de poblacion victima disponibles.'
          />
          <DistributionCard
            title='Enfoque de discapacidad'
            items={summary.disabilityDistribution}
            loading={loading}
            emptyMessage='No hay datos de discapacidad disponibles.'
          />
          <DistributionCard
            title='Factores de riesgo'
            items={summary.riskDistribution}
            loading={loading}
            emptyMessage='No hay datos de factores de riesgo disponibles.'
          />
        </section>

        <section className='admin-characterized-analysis__flags'>
          <article className='admin-characterized-analysis__flag-card'>
            <span>Cuenta con RUV</span>
            <strong>{loading ? '--' : formatNumber(summary.victimRegistry)}</strong>
          </article>
          <article className='admin-characterized-analysis__flag-card'>
            <span>Se identifica como campesino</span>
            <strong>{loading ? '--' : formatNumber(summary.farmerPopulation)}</strong>
          </article>
          <article className='admin-characterized-analysis__flag-card'>
            <span>NNA en restablecimiento de derechos</span>
            <strong>{loading ? '--' : formatNumber(summary.nnaRightsProcess)}</strong>
          </article>
        </section>
      </div>
    </section>
  );
}

export default AdminCharacterizedAnalysis;
